import React from 'react';
import { Sparkles, X } from 'lucide-react';
import type { ManualSkill } from '@Types/skills';
import {
  ADP_EVENTS,
  getBernoulliAdp,
  trackAdpWaypoint,
} from '@/services/analytics/adp';

interface SkillChipStripProps {
  active: ManualSkill[];
  onRemove: (skillId: string) => void;
}

export const SkillChipStrip = ({ active, onRemove }: SkillChipStripProps) => {
  if (active.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 mb-2">
      {active.map(skill => {
        const isTenant = skill.scope === 'tenant';
        return (
          <div
            key={skill.skill_id}
            className={`group relative inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full border text-xs ${
              isTenant
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-[#F5F5F5] border-[#E0E0E0] text-brand-dark'
            }`}>
            <Sparkles className="h-3 w-3" />
            <span className="font-medium">{skill.name}</span>
            {isTenant && (
              <span className="text-[10px] uppercase tracking-wide px-1 rounded bg-emerald-100/80">
                Tenant
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                trackAdpWaypoint(
                  getBernoulliAdp(),
                  ADP_EVENTS.SKILLS_REMOVE_ACTIVE,
                  {
                    skill_id: skill.skill_id,
                    skill_name: skill.name,
                    scope: skill.scope,
                  },
                );
                onRemove(skill.skill_id);
              }}
              className="ml-0.5 p-0.5 rounded-full hover:bg-[#E0E0E0]"
              aria-label={`Remove ${skill.name}`}>
              <X className="h-3 w-3" />
            </button>
            {skill.description && (
              <div
                role="tooltip"
                className="absolute left-1/2 -translate-x-1/2 bottom-full pb-2 z-50 w-80 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-150">
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rotate-45 bg-white border-r border-b border-slate-200/70" />
                <div className="relative rounded-lg bg-white border border-slate-200/70 shadow-[0_8px_24px_-8px_rgba(15,23,42,0.18)] overflow-hidden">
                  <div className="flex items-center gap-1.5 px-3 py-2 bg-[#F5F5F5] border-b border-slate-200/60">
                    <Sparkles className="h-3 w-3 text-brand-dark" />
                    <span className="text-[11px] font-semibold tracking-wide text-brand-dark uppercase">
                      {skill.name}
                    </span>
                  </div>
                  <div className="px-3 py-2.5">
                    <p className="text-[11.5px] leading-relaxed text-[#252F33]/85 whitespace-pre-wrap">
                      {skill.description}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
